import apiClient from '../base/apiClient';

export const userApi = {
  getListUsers: async (params: { filter?: string; page?: number; perPage?: number } = {}) => {
    const { filter = '', page = 0, perPage = 100 } = params;
    const response = await apiClient.get('/Administration/Account/ListUsers', { params: { filter, page, count: perPage } });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    const users = response.data.users || [];
    return {
      data: users.map((user: any) => ({
        ...user,
        profileImageUrl: user.profileImageUrl ? `${apiClient.defaults.baseURL}${user.profileImageUrl}` : "",
        profileThumbnailUrl: user.thumbnailImageUrl ? `${apiClient.defaults.baseURL}${user.thumbnailImageUrl}` : "",
        
      })),
      total: response.data.total || users.length,
    };
  },

  addAccount: async (params: { data: any }) => {
    const { data } = params;
    const response = await apiClient.post('/Administration/Account/Add', {
      username: data.username,
      password: data.password,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
    });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return {
      data: {
        userId: response.data.id || 0,
        username: data.username,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
      },
    };
  },

  deleteUser: async (params: { id: number }) => {
    const { id: userId } = params;
    const response = await apiClient.delete('/Administration/Account/Delete', { params: { userId } });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { id: userId } };
  },

  updateUser: async (params: { id: number; data: any }) => {
    const { id, data } = params;
    const response = await apiClient.put('/Administration/Account/Update', {
      userId: id,
      email: data.email,
      firstName: data.firstName,
      middleName: data.middleName || '',
      lastName: data.lastName,
      phoneNumber: data.phoneNumber,
      birthday: data.birthday,
      facebookProfile: data.facebookProfile,
      nationality: data.nationality || '',
    });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { ...data, userId: id } };
  },

  changePassword: async (params: { id: number; currentPassword: string; newPassword: string }) => {
    const { id, currentPassword, newPassword } = params;
    const response = await apiClient.patch('/Administration/Account/ChangePassword', {
      userId: id,
      currentPassword,
      newPassword,
    });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { success: true } };
  },

  setAccountLocked: async (params: { userId: number; locked: boolean }) => {
    const { userId, locked } = params;
    const response = await apiClient.patch('/Administration/Account/SetLocked', null, { params: { userId, locked } });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { success: true } };
  },

  setEmailVerified: async (params: { userId: number; verified: boolean }) => {
    const { userId, verified } = params;
    const response = await apiClient.patch('/Administration/Account/SetEmailVerified', null, { params: { userId, verified } });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { success: true } };
  },

  setContractorVerified: async (params: { userId: number; verified: boolean }) => {
    const { userId, verified } = params;
    const response = await apiClient.patch('/Administration/Account/SetContractorVerified', null, { params: { userId, verified } });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { success: true } };
  },

  setDocumentStatus: async (params: { userId: number; documentId: number; status: number; notes?: string }) => {
    const { userId, documentId, status, notes = '' } = params;
    const response = await apiClient.get('/Administration/Account/SetDocumentStatus', {
      params: { userId, documentId, status, notes },
    });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { success: true } };
  },

  setClientPostAllowed: async (params: { userId: number; postAllowed: boolean }) => {
    const { userId, postAllowed } = params;
    const response = await apiClient.patch('/Administration/Account/SetClientPostAllowed', null, { params: { userId, postAllowed } });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { success: true } };
  },

  setContractorSpecial: async (params: { userId: number; verified: boolean }) => {
    const { userId, verified } = params;
    const response = await apiClient.patch('/Administration/Account/SetContractorSpecial', null, { params: { userId, verified } });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { success: true } };
  },

  getUserTransactions: async (params: { userId: number; filter?: string; transactionTypes?: string; page?: number; perPage?: number }) => {
    const { userId, filter = '', transactionTypes = '', page = 0, perPage = 100 } = params;
    const response = await apiClient.get('/Administration/Transaction/GetByUserId', {
      params: { userId, filter, transactionTypes, page, count: perPage },
    });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    const transactions = response.data.transactions || [];
    return {
      data: {
        transactions: transactions.map((transaction: any) => ({
          id: transaction.transactionId || 0,
          timestamp: transaction.timestamp || '',
          transactionType: transaction.transactionType || '',
          orderId: transaction.orderId || '',
          orderDetails: transaction.orderDetails || '',
          description: transaction.description || '',
          amount: transaction.amount || 0,
          previousCredits: transaction.previousCredits || 0,
          newCredits: transaction.newCredits || 0,
          userId: transaction.userId || 0,
        })),
        total: response.data.total || transactions.length,
      },
    };
  },

  uploadProfileImageUser: async (params: { userId: number; file: File }) => {
    const { userId, file } = params;
    const formData = new FormData();
    formData.append('profileImage', file);
    const response = await apiClient.post(`/Administration/Account/UploadProfileImage?userId=${userId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { success: true, profileImageUrl: response.data.profileImageUrl || '' } };
  },

  adjustUserCredits: async (params: { userId: number; transactionType: number; amount: number; reason: string }) => {
    const { userId, transactionType, amount, reason } = params;
    const response = await apiClient.get(
      `/Administration/Transaction/AdjustCredit`,
      {
        params: {
          userId,
          transactionType,
          amount,
          reason,
        },
        withCredentials: true,
      }
    );

    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    return { data: response.data };
  },

};